import { useMemo, useCallback, useEffect, useState, useRef } from 'react'
import { useQueryParams, useLocation } from '@fs/zion-router'
import cloneDeep from 'lodash.clonedeep'
import {
  getMostRecentFieldValue,
  getImage,
  getNestedSubelementsByType,
  createHighlight,
  processRectangle,
  compareRect,
  useSlsStateContext,
} from '@fs/zion-sls'
import { useFeatureFlag } from '@fs/zion-flags'
import { getImageStuff } from '../../../../shared/services/sls/slsService'
import generateCitation from '../../../../shared/utils/generateCitation'
import { useOverlayContext } from '../../../../shared/providers/overlayContextProvider/OverlayContext'
import { useR2StateContext } from '../../../providers/r2Provider/R2Context'
import { useSharedBrowserStateContext } from '../../../../shared/providers/sharedBrowserProvider/SharedBrowserContext'
import { errorCollector } from '../../../../shared/utils/utils'

const theSameRect =
  (rect, compareImageIdAsWell = false) =>
  (r) => {
    if (!r) return false // If the rect is null, don't compare
    return compareRect(rect, r, compareImageIdAsWell)
  }

const getApidFromImageArk = ({ imageArk, thumbnails }) => {
  const thumbnail = thumbnails.find((t) => t.ark === imageArk)
  return thumbnail?.id
}

const useDownloadOverlayData = (setAllHighlights) => {
  const {
    query: { view: queryView },
  } = useQueryParams()
  const state = useSlsStateContext()
  const sharedBrowserState = useSharedBrowserStateContext()
  const location = useLocation()
  const r2State = useR2StateContext()
  const { selectPersonToAttachToTreeInfoSheetOverlay } = useOverlayContext()
  const [imageStuff, setImageStuff] = useState(null)
  const [imageStuffError, setImageStuffError] = useState(null)
  const recordsCache = useRef(new Map())
  const currentImage = useMemo(() => getImage(state?.images, state?.currentImageArk) || {}, [state])
  const imageArk = location.pathname.split('/').pop()
  const transcriptFromStuffRecordsFlag = useFeatureFlag('rip_sharedImageViewer_transcriptFromStuffRecords')

  // Helper function to split fields into individual word subfields
  const splitField = useCallback((field) => {
    const fields = []
    const fieldValue = getMostRecentFieldValue(field)
    const words = fieldValue?.split(' ') || []

    if (!words || words.length === 1) return [field]

    words.forEach((w, idx) => {
      fields.push({
        ...cloneDeep(field),
        rects: [field?.rects?.[idx]],
        fieldValues: [{ ...field.fieldValues[0], origValue: { text: w } }],
        id: `${field.id}_${idx}`, // Unique ID for each word split
      })
    })
    return fields
  }, [])

  // Process each element to generate individual highlights for split fields
  const splitFields = useCallback(
    (element, highlights) => {
      const fieldsArray = splitField(element)
      fieldsArray.forEach((fa, i) => {
        if (fa.rects && !highlights.find((h) => h.fieldId === fa.id && theSameRect(h.rect, fa.rects[0]))) {
          highlights.push(
            createHighlight(
              element,
              processRectangle({
                rect: fa?.rects[0],
                imageId: fa?.rects[0]?.imageId,
                fieldId: fa.id,
                fieldType: fa.fieldType,
                elementType: fa.elementType,
                personIds: [fa.personId],
                recordId: fa.recordId,
              }),
              i
            )
          )
        }
      })
      return fieldsArray
    },
    [splitField]
  )

  /**
   * Split sls field values by spaces and collect highlights associated with fields.
   * @returns {Array} field objects and clones for multi-word fields
   */
  const generateFieldsFromSls = useCallback(() => {
    const allHighlightsTemp = []

    const reduceFunction = (acc, element) => {
      if (element.fieldType === 'NAME') return acc
      if (element.elementType === 'FIELD') {
        acc = acc.concat(splitFields(element, allHighlightsTemp))
      } else if (element?.subElements) {
        element.subElements.forEach((se) => {
          if (se.subElements?.[0]) {
            acc = acc.concat(splitFields(se.subElements?.[0], allHighlightsTemp))
          }
        })
      }
      return acc
    }

    let fieldsTemp = currentImage?.floatingElements?.reduce(reduceFunction, []) || []
    // Merge in names from PERSON elements that have stuffTokenOffsets (originated in the transcript)
    const namesInImage = getNestedSubelementsByType(currentImage, 'NAME', false)
    const nameFieldsFromPersons = namesInImage.reduce(reduceFunction, [])
    fieldsTemp = fieldsTemp.concat(nameFieldsFromPersons.filter((f) => f.stuffTokenOffsets))
    const eventFieldsFromPersons = getNestedSubelementsByType(currentImage, 'EVENT', false)
      .flatMap((ev) => getNestedSubelementsByType(ev, 'FIELD'))
      .filter((f) => f.stuffTokenOffsets)
      .flatMap((f) => splitFields(f, allHighlightsTemp))
    fieldsTemp = fieldsTemp.concat(eventFieldsFromPersons)

    if (!selectPersonToAttachToTreeInfoSheetOverlay?.isOpen && setAllHighlights) {
      setAllHighlights(allHighlightsTemp)
    }
    if (fieldsTemp)
      fieldsTemp.sort((a, b) => {
        if (!a.stuffTokenOffsets && !b.stuffTokenOffsets) return 0
        if (!a.stuffTokenOffsets && b.stuffTokenOffsets) return 1
        if (a.stuffTokenOffsets && !b.stuffTokenOffsets) return -1
        return (a.stuffTokenOffsets?.[0] || 0) - (b.stuffTokenOffsets?.[0] || 0)
      })
    return fieldsTemp || []
  }, [currentImage, selectPersonToAttachToTreeInfoSheetOverlay?.isOpen, setAllHighlights, splitFields])

  const splitByTextSpaces = useCallback(
    (token, allHighlightsTemp) => {
      const fields = []
      const words = token.text?.split(' ') || []

      const imageHeight = imageStuff?.stuff?.pages?.[0]?.pageHeight
      const imageWidth = imageStuff?.stuff?.pages?.[0]?.pageWidth
      let rectSplit = token.rect ? token.rect.split(',').map((pos) => Number.parseInt(pos, 10)) : []
      if (imageHeight && imageWidth) {
        // Convert rect pixel coordinates to ratio coordinates by dividing x values by width and y values by height
        rectSplit = rectSplit.map((pos, idx) => {
          const denominator = idx === 0 || idx === 2 ? imageWidth : imageHeight
          return pos / denominator
        })
      }
      const rect =
        rectSplit.length === 4
          ? {
              x1: rectSplit[0],
              y1: rectSplit[1],
              x2: rectSplit[0] + rectSplit[2],
              y2: rectSplit[1] + rectSplit[3],
              imageId: state.currentImageArk,
            }
          : null

      words.forEach((w, idx) => {
        if (rect && idx === 0) {
          allHighlightsTemp.push(
            createHighlight(
              { id: state.currentImageArk },
              {
                ...rect,
                fieldId: words.length === 1 ? token.id : `${token.id}_${idx}`,
                imageId: state.currentImageArk,
              },
              allHighlightsTemp.length
            )
          )
        }

        fields.push({
          rects: [token?.rect],
          fieldValues: [{ id: token.id, origValue: { text: w } }],
          id: words.length === 1 ? token.id : `${token.id}_${idx}`, // Unique ID for each word split
        })
      })
      return fields
    },
    [imageStuff, state.currentImageArk]
  )

  /**
   * Create sls-like field objects from stuff data.
   * @returns {Array} field objects with field values and rects.
   */
  const generateFieldsFromImageStuff = useCallback(() => {
    const allHighlightsTemp = []

    const reduceFunction = (acc, token) => {
      acc = acc.concat(splitByTextSpaces(token, allHighlightsTemp))
      return acc
    }

    const tokens = imageStuff?.stuff?.regions?.flatMap((region) => region.lines.flatMap((line) => line.tokens))
    const fieldsTemp = tokens?.reduce(reduceFunction, []) || []
    if (!selectPersonToAttachToTreeInfoSheetOverlay?.isOpen && setAllHighlights) {
      setAllHighlights(allHighlightsTemp)
    }
    return fieldsTemp
  }, [
    imageStuff?.stuff?.regions,
    selectPersonToAttachToTreeInfoSheetOverlay?.isOpen,
    setAllHighlights,
    splitByTextSpaces,
  ])

  // Split up all fields into individual word/rect fields.
  const fields = useMemo(() => {
    return transcriptFromStuffRecordsFlag.isOn ? generateFieldsFromImageStuff() : generateFieldsFromSls()
  }, [transcriptFromStuffRecordsFlag.isOn, generateFieldsFromImageStuff, generateFieldsFromSls])

  // Generate plain text transcript by joining values of all fields
  const plainTextTranscript = useMemo(() => {
    return fields.map((field) => getMostRecentFieldValue(field)).join(' ')
  }, [fields])

  let citation = ''

  const {
    groupData: { creators: creator, custodians: custodian, dgsNumber: imageGroupNumber, coverages },
  } = r2State

  const imageCount = sharedBrowserState.unifiedApidList?.length
  const imageApid = getApidFromImageArk({
    imageArk,
    thumbnails: sharedBrowserState?.thumbnails,
  })
  const imageIndex = imageApid
    ? sharedBrowserState.unifiedApidList?.indexOf(imageApid)
    : sharedBrowserState.focusedIndex

  const place = coverages?.find((c) => c?.place)?.place

  citation = generateCitation({
    creator,
    custodian,
    imageCount,
    imageIndex,
    location,
    queryView,
    imageGroupNumber,
    place,
  })

  useEffect(() => {
    const fetchTranscript = async () => {
      if (state.currentImageArk && queryView === 'fullText' && transcriptFromStuffRecordsFlag.isOn) {
        setImageStuffError(null)
        // Check cache first
        if (recordsCache.current.has(state.currentImageArk)) {
          setImageStuff(recordsCache.current.get(state.currentImageArk))
          return
        }

        try {
          // Fetch and cache the result
          const result = await getImageStuff(state.currentImageArk)
          setImageStuff(result)
          recordsCache.current.set(state.currentImageArk, result)
        } catch (e) {
          setImageStuffError(true)
          errorCollector(e, state.currentImageArk, { codeId: 41 })
        }
      }
    }
    fetchTranscript()
  }, [state.currentImageArk, queryView, transcriptFromStuffRecordsFlag.isOn])

  return {
    citation,
    plainTextTranscript,
    fields,
    imageStuffError,
  }
}

export default useDownloadOverlayData
